import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button, Checkbox, Input } from '@/components/ui';
import type {
  FilterField,
  RoutingPattern,
  SupportedEventType,
} from '../../types/integrations';
import {
  buildPattern,
  newRowId,
  operatorsForType,
  parsePattern,
  validateRow,
  OPERATOR_LABELS,
  type FieldFilterRow,
  type PatternOperator,
} from './pattern-helpers';

interface PatternBuilderProps {
  /**
   * Routable fields exposed by the provider — normalized to the rich shape
   * by the caller (see `normalizeFilterFields`).
   */
  fields: FilterField[];
  /** Event types the provider declares; used by the pin chip group. */
  supportedEventTypes: SupportedEventType[];
  /** Current pattern (controlled). Empty object means "no rows yet". */
  value: RoutingPattern;
  onChange: (next: RoutingPattern) => void;
  /** External submit-error messages keyed by row id. */
  rowErrors?: Record<string, string>;
}

/**
 * Visual rule pattern composer. Owns the local row/pin/JSON-mode state and
 * emits the built `RoutingPattern` upward whenever it changes. The parent
 * keeps the canonical pattern in state so a parse round-trip survives unmount.
 */
export default function PatternBuilder({
  fields,
  supportedEventTypes,
  value,
  onChange,
  rowErrors,
}: PatternBuilderProps) {
  // Local UI state. The pattern in `value` is the source of truth; rows /
  // pinnedEventTypes are derived once the field catalog is available, then
  // driven locally.
  //
  // `fields` arrives asynchronously from the providers query, and parsing
  // against an empty catalog classifies every leaf as unparseable — which
  // would strand anyone editing a saved rule in the raw-JSON banner. So hold
  // off until the catalog lands, then adopt the parse exactly once.
  const initial = useMemo(() => parsePattern(value, fields), [value, fields]);

  const [rows, setRows] = useState<FieldFilterRow[]>(initial.rows);
  const [pinnedEventTypes, setPinnedEventTypes] = useState<string[]>(
    initial.pinnedEventTypes,
  );
  const adoptedRef = useRef(fields.length > 0);

  useEffect(() => {
    if (adoptedRef.current || fields.length === 0) return;
    adoptedRef.current = true;
    setRows(initial.rows);
    setPinnedEventTypes(initial.pinnedEventTypes);
  }, [fields, initial]);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonDraft, setJsonDraft] = useState(
    JSON.stringify(value ?? {}, null, 2),
  );
  const [jsonError, setJsonError] = useState<string | null>(null);

  const hasUnparseable = !!initial.unparseable;

  // Push every form change up to the parent as a fresh built pattern. The
  // parent decides when to send it to the server.
  const emit = (
    nextRows: FieldFilterRow[],
    nextPinned: string[],
  ) => {
    onChange(buildPattern(nextRows, nextPinned));
  };

  const togglePinned = (eventType: string) => {
    const next = pinnedEventTypes.includes(eventType)
      ? pinnedEventTypes.filter((e) => e !== eventType)
      : [...pinnedEventTypes, eventType];
    setPinnedEventTypes(next);
    emit(rows, next);
  };

  const addRow = () => {
    const firstField = fields[0];
    const op: PatternOperator = firstField
      ? operatorsForType(firstField.type)[0]
      : 'equals';
    const next: FieldFilterRow = {
      id: newRowId(),
      fieldPath: firstField?.path ?? '',
      operator: op,
      value: defaultValueForOperator(op),
    };
    const nextRows = [...rows, next];
    setRows(nextRows);
    emit(nextRows, pinnedEventTypes);
  };

  const updateRow = (id: string, patch: Partial<FieldFilterRow>) => {
    const nextRows = rows.map((r) => (r.id === id ? { ...r, ...patch } : r));
    setRows(nextRows);
    emit(nextRows, pinnedEventTypes);
  };

  const removeRow = (id: string) => {
    const nextRows = rows.filter((r) => r.id !== id);
    setRows(nextRows);
    emit(nextRows, pinnedEventTypes);
  };

  const enterJsonMode = () => {
    setJsonDraft(JSON.stringify(value ?? {}, null, 2));
    setJsonError(null);
    setJsonMode(true);
  };

  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonDraft) as RoutingPattern;
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new Error('Pattern must be a JSON object');
      }
      const parts = parsePattern(parsed, fields);
      setRows(parts.rows);
      setPinnedEventTypes(parts.pinnedEventTypes);
      onChange(parsed);
      setJsonError(null);
      setJsonMode(false);
    } catch (err) {
      setJsonError((err as Error).message || 'Invalid JSON');
    }
  };

  const cancelJson = () => {
    setJsonDraft(JSON.stringify(value ?? {}, null, 2));
    setJsonError(null);
    setJsonMode(false);
  };

  if (jsonMode) {
    return (
      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium">Raw pattern (JSON)</span>
            <div className="space-x-2">
              <Button type="button" variant="ghost" size="sm" onClick={cancelJson}>
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={applyJson}>
                Apply
              </Button>
            </div>
          </div>
          <textarea
            value={jsonDraft}
            onChange={(e) => setJsonDraft(e.target.value)}
            className="w-full h-64 font-mono text-xs border border-input rounded-md p-3 bg-background"
            spellCheck={false}
          />
          {jsonError && (
            <p className="text-sm text-destructive mt-1">{jsonError}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {hasUnparseable && (
        <p className="text-sm text-muted-foreground">
          This pattern contains fields the builder doesn&rsquo;t recognise — switch to{' '}
          <button
            type="button"
            className="underline"
            onClick={enterJsonMode}
          >
            Advanced (raw JSON)
          </button>{' '}
          to edit them.
        </p>
      )}

      {/* Pinned event types */}
      {supportedEventTypes.length > 0 && (
        <div>
          <div className="text-sm font-medium mb-2">
            Pin to event types{' '}
            <span className="text-muted-foreground font-normal">
              (optional &mdash; leave empty to match all)
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {supportedEventTypes.map((evt) => {
              const checked = pinnedEventTypes.includes(evt.eventType);
              return (
                <label
                  key={evt.eventType}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm cursor-pointer transition ${
                    checked
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-border bg-background text-foreground hover:bg-muted'
                  }`}
                  title={evt.description}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => togglePinned(evt.eventType)}
                  />
                  <span>{evt.eventType}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Field filters */}
      <div>
        <div className="text-sm font-medium mb-2">Field filters</div>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground mb-2">
            No filters yet. Without any filters the rule matches every event
            of the pinned types (or every event, if no types are pinned).
          </p>
        ) : (
          <div className="space-y-2">
            {rows.map((row) => (
              <FieldFilterRowEditor
                key={row.id}
                row={row}
                fields={fields}
                onChange={(patch) => updateRow(row.id, patch)}
                onRemove={() => removeRow(row.id)}
                error={rowErrors?.[row.id]}
              />
            ))}
          </div>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addRow}
          disabled={fields.length === 0}
          className="mt-2"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add filter
        </Button>
        {fields.length === 0 && (
          <p className="text-xs text-muted-foreground mt-2">
            This provider hasn&rsquo;t declared any filter fields. Use{' '}
            <button
              type="button"
              className="underline"
              onClick={enterJsonMode}
            >
              Advanced (raw JSON)
            </button>{' '}
            to author the pattern directly.
          </p>
        )}
      </div>

      {/* Advanced disclosure */}
      <details className="text-sm">
        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
          Advanced &middot; preview &amp; edit raw JSON
        </summary>
        <div className="mt-2 space-y-2">
          <pre className="rounded-md border bg-muted/30 p-3 text-xs overflow-x-auto">
            {JSON.stringify(value ?? {}, null, 2)}
          </pre>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={enterJsonMode}
          >
            Edit as JSON
          </Button>
        </div>
      </details>
    </div>
  );
}

interface FieldFilterRowEditorProps {
  row: FieldFilterRow;
  fields: FilterField[];
  onChange: (patch: Partial<FieldFilterRow>) => void;
  onRemove: () => void;
  error?: string;
}

function FieldFilterRowEditor({
  row,
  fields,
  onChange,
  onRemove,
  error,
}: FieldFilterRowEditorProps) {
  const field = fields.find((f) => f.path === row.fieldPath);
  const ops = field ? operatorsForType(field.type) : (['equals'] as PatternOperator[]);

  const handleFieldChange = (newPath: string) => {
    const newField = fields.find((f) => f.path === newPath);
    if (!newField) {
      onChange({ fieldPath: newPath });
      return;
    }
    const allowedOps = operatorsForType(newField.type);
    const nextOp: PatternOperator = allowedOps.includes(row.operator)
      ? row.operator
      : allowedOps[0];
    // Reset the value whenever the operator changes — operators take
    // different value shapes, so carrying over the old one would lie.
    onChange({
      fieldPath: newPath,
      operator: nextOp,
      value: nextOp === row.operator ? row.value : defaultValueForOperator(nextOp),
    });
  };

  const handleOperatorChange = (newOp: PatternOperator) => {
    onChange({ operator: newOp, value: defaultValueForOperator(newOp) });
  };

  return (
    <div className="rounded-md border bg-background p-3">
      <div className="flex flex-wrap items-start gap-2">
        <select
          value={row.fieldPath}
          onChange={(e) => handleFieldChange(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm min-w-[12rem]"
        >
          {fields.map((f) => (
            <option key={f.path} value={f.path}>
              {f.path}
            </option>
          ))}
        </select>

        {field?.keyed && (
          <>
            <Input
              value={row.tagKey ?? ''}
              onChange={(e) => onChange({ tagKey: e.target.value })}
              placeholder={field.keyed.keyLabel}
              aria-label={field.keyed.keyLabel}
              list={`${row.id}-keys`}
              className="h-9 w-[9rem]"
            />
            {field.keyed.keyExamples && field.keyed.keyExamples.length > 0 && (
              <datalist id={`${row.id}-keys`}>
                {field.keyed.keyExamples.map((k) => (
                  <option key={k} value={k} />
                ))}
              </datalist>
            )}
          </>
        )}

        <select
          value={row.operator}
          onChange={(e) => handleOperatorChange(e.target.value as PatternOperator)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          {ops.map((op) => (
            <option key={op} value={op}>
              {OPERATOR_LABELS[op]}
            </option>
          ))}
        </select>

        <div className="flex-1 min-w-[12rem]">
          <ValueInput row={row} field={field} onChange={onChange} />
        </div>

        <button
          type="button"
          onClick={onRemove}
          className="h-9 w-9 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          aria-label="Remove filter"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      {field?.description && (
        <p className="text-xs text-muted-foreground mt-2">{field.description}</p>
      )}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}

function defaultValueForOperator(op: PatternOperator): unknown {
  switch (op) {
    case 'equals':
    case 'prefix':
    case 'contains':
    case 'wildcard':
      return '';
    case 'one-of':
    case 'anything-but':
      return [];
    case 'exists':
      return true;
    case 'numeric':
      return { op: '=', n: 0 };
  }
}

interface ValueInputProps {
  row: FieldFilterRow;
  field?: FilterField;
  onChange: (patch: Partial<FieldFilterRow>) => void;
}

function ValueInput({ row, field, onChange }: ValueInputProps) {
  if (row.operator === 'exists') {
    const checked = row.value === true;
    return (
      <label className="inline-flex items-center gap-2 text-sm h-9">
        <Checkbox
          checked={checked}
          onCheckedChange={(v) => onChange({ value: Boolean(v) })}
        />
        <span>field exists</span>
      </label>
    );
  }

  if (row.operator === 'numeric') {
    const v = (row.value ?? {}) as { op?: string; n?: number };
    return (
      <div className="flex items-center gap-2">
        <select
          value={v.op ?? '='}
          onChange={(e) => onChange({ value: { ...v, op: e.target.value } })}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          {['=', '>', '>=', '<', '<='].map((op) => (
            <option key={op} value={op}>
              {op}
            </option>
          ))}
        </select>
        <Input
          type="number"
          value={Number.isFinite(v.n as number) ? String(v.n) : ''}
          onChange={(e) =>
            onChange({ value: { ...v, n: Number(e.target.value) } })
          }
          placeholder="0"
          className="h-9"
        />
      </div>
    );
  }

  if (row.operator === 'one-of' || row.operator === 'anything-but') {
    const arr = Array.isArray(row.value) ? (row.value as Array<string | number>) : [];
    // Comma-separated input — lightest UI that's still usable. Power users
    // can switch to Advanced/JSON for whitespace-sensitive values.
    const text = arr.join(', ');
    return (
      <Input
        value={text}
        onChange={(e) => {
          const next = e.target.value
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
          const coerced = field?.type === 'number' ? next.map(Number) : next;
          onChange({ value: coerced });
        }}
        placeholder="value1, value2"
        className="h-9"
      />
    );
  }

  // equals — enum dropdown when available; otherwise free text.
  if (row.operator === 'equals' && field?.enum && field.enum.length > 0) {
    return (
      <select
        value={String(row.value ?? '')}
        onChange={(e) => {
          const raw = e.target.value;
          const coerced = field.type === 'number' ? Number(raw) : raw;
          onChange({ value: coerced });
        }}
        className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
      >
        <option value="">Pick a value…</option>
        {field.enum.map((opt) => (
          <option key={String(opt)} value={String(opt)}>
            {String(opt)}
          </option>
        ))}
      </select>
    );
  }

  // equals / prefix / contains / wildcard — plain text. Only `equals` coerces
  // to a number; the text operators always compare against strings.
  const isTextOperator =
    row.operator === 'prefix' || row.operator === 'contains' || row.operator === 'wildcard';
  const coerceToNumber = field?.type === 'number' && !isTextOperator;

  return (
    <Input
      type={coerceToNumber ? 'number' : 'text'}
      value={row.value == null ? '' : String(row.value)}
      onChange={(e) => {
        const raw = e.target.value;
        onChange({ value: coerceToNumber ? Number(raw) : raw });
      }}
      placeholder={placeholderFor(row.operator, field)}
      className="h-9"
    />
  );
}

function placeholderFor(operator: PatternOperator, field?: FilterField): string {
  // The text operators need their own hints — a field example like
  // "[CRIT] API error rate above threshold" is actively misleading next to
  // "contains", where the point is to type a fragment.
  switch (operator) {
    case 'prefix':
      return 'prefix to match';
    case 'contains':
      return 'text to find anywhere';
    case 'wildcard':
      return 'e.g. billing*prod';
    default:
      break;
  }
  // A keyed field's value box holds one value under the chosen key, so the
  // field-level example (a whole map) is the wrong hint for it.
  if (field?.keyed) return 'value for this key';
  // Guard the same way for any other non-primitive example — String() on an
  // object renders the useless "[object Object]".
  const ex = field?.example;
  return ex != null && typeof ex !== 'object' ? `e.g. ${String(ex)}` : 'value';
}

// Re-export for parent forms that need to validate before submit.
export { validateRow };
